import React, { useEffect, useState } from 'react';
import { Outlet, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { LogOut, UserCircle, FolderCheck, Gauge, ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Logo } from '@/components/shared/Logo';
import LanguageSwitcher from '@/components/layout/LanguageSwitcher';
import { Button } from '@/components/ui/button';
import { useApp } from '@/context/AppContext';
import { InstallCTH } from '@/components/shared/InstallCTH';
import { api } from '@/lib/api';

/**
 * Iter 41 — Bare full-screen shell for the Evaluated user (Role 4).
 *
 * No sidebar, no portal navigation. Just:
 *   • Logo (left)
 *   • Language switcher + Logout button (right)
 *   • Outlet rendered full-bleed below
 *
 * Also implements the Informed Consent hard gate:
 *   - If the logged-in user has not yet accepted consent, any route except
 *     `/me/consent` is redirected to `/me/consent`.
 *   - Once accepted (`user.consent_accepted_at` set), they can navigate freely
 *     within the limited set of routes (assignments list, single assessment,
 *     thank-you screen).
 */
export const EvaluatedLayout = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useApp();

  // Header shortcut for delegated WR editors — re-checked on navigation so a
  // revoked grant disappears immediately. Grants no extra privileges.
  // (Hooks must run before the conditional redirects below.)
  const [wrEditor, setWrEditor] = useState(false);
  const [hasPerf, setHasPerf] = useState(false);
  useEffect(() => {
    let alive = true;
    api.get('/wr/my-access')
      .then((r) => { if (alive) setWrEditor(!!(r.data?.enabled && r.data?.can_edit)); })
      .catch(() => { if (alive) setWrEditor(false); });
    api.get('/me/performance')
      .then((r) => { if (alive) setHasPerf(((r.data?.my_results || []).length + (r.data?.my_team || []).length) > 0); })
      .catch(() => { if (alive) setHasPerf(false); });
    return () => { alive = false; };
  }, [location.pathname]);

  // Hard consent gate
  const consentAccepted = !!user?.consent_accepted_at;
  const onConsentRoute = location.pathname.startsWith('/me/consent');
  if (!consentAccepted && !onConsentRoute) {
    return <Navigate to="/me/consent" replace />;
  }

  // Iter 41.34 — soft "show profile once after consent" gate. If the user has
  // accepted consent but hasn't seen the personal-profile intro yet, redirect
  // them to /me/profile the first time they land anywhere else under /me.
  // The Topbar Profile link still works, and the profile page itself sets
  // `profile_intro_seen=true` on submit/skip.
  const introSeen = user?.profile_intro_seen === true;
  const onProfileRoute = location.pathname.startsWith('/me/profile');
  const indexRoute = location.pathname === '/me' || location.pathname === '/me/';
  if (consentAccepted && !introSeen && !onConsentRoute && !onProfileRoute && indexRoute) {
    return <Navigate to="/me/profile" replace />;
  }

  const doLogout = async () => {
    try { await logout?.(); } catch { /* ignore */ }
    navigate('/login', { replace: true });
  };

  // Workforce Records is a data-dense matrix workspace — give it the full
  // viewport instead of the narrow reading-width used by assessment pages.
  const wideWorkspace = location.pathname.startsWith('/me/workforce-records');
  const shellWidth = wideWorkspace ? 'max-w-[1800px]' : 'max-w-5xl';

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header
        className="sticky top-0 z-40 bg-background/95 backdrop-blur-sm border-b border-border"
        data-testid="evaluated-topbar"
      >
        <div className={`${shellWidth} mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-3`}>
          <button type="button" onClick={() => navigate('/me')} className="cursor-pointer shrink-0"
                  aria-label="Home" data-testid="evaluated-logo-home">
            <Logo size="sm" />
          </button>
          <div className="flex items-center gap-1 sm:gap-2 min-w-0">
            {['client_admin', 'client_hr', 'client_viewer'].includes(user?.role) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/client')}
                data-testid="evaluated-back-to-admin"
                className="gap-1.5"
              >
                <ArrowLeft className="h-4 w-4" />
                <span className="hidden lg:inline">{t('evaluated.backToAdmin', { defaultValue: 'Volver a administración' })}</span>
              </Button>
            )}
            {wrEditor && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/me/workforce-records')}
                data-testid="evaluated-wr-link"
                className="gap-1.5"
              >
                <FolderCheck className="h-4 w-4" />
                <span className="hidden lg:inline">Workforce Records</span>
              </Button>
            )}
            {hasPerf && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/me/performance')}
                data-testid="evaluated-performance-link"
                className="gap-1.5"
              >
                <Gauge className="h-4 w-4" />
                <span className="hidden lg:inline">{t('talentperformance.me.title', { defaultValue: 'My Performance' })}</span>
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/me/profile')}
              data-testid="evaluated-profile-link"
              className="gap-1.5"
            >
              <UserCircle className="h-4 w-4" />
              <span className="hidden lg:inline">{t('pages.profile.title', { defaultValue: 'Profile' })}</span>
            </Button>
            <div className="shrink-0 ml-1 pl-2 border-l border-border" data-testid="evaluated-lang-switcher">
              <LanguageSwitcher compact />
            </div>
            <InstallCTH className="shrink-0" />
            <Button
              variant="outline"
              size="sm"
              onClick={doLogout}
              data-testid="evaluated-logout"
              className="gap-1.5 shrink-0"
            >
              <LogOut className="h-4 w-4" />
              <span className="hidden lg:inline">{t('common.logout', { defaultValue: 'Log out' })}</span>
            </Button>
          </div>
        </div>
      </header>
      <main className="flex-1 w-full">
        <div className={`${shellWidth} mx-auto px-6 lg:px-8 py-8`}>
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default EvaluatedLayout;
